import { writable } from 'svelte/store';
import { max, min, cloneDate, subtractDuration, addDuration, listView, getElementWithPayload, getPayload, timelineView, ancestor, createDuration, isFunction, toEventWithLocalDates, toISOString, toViewWithLocalDates, cloneEvent, assign, toLocalDate, rect, listen, copyTime, subtractDay, addDay, runAll, bgEvent, helperEvent, noop } from '@kokoro-calendar/core';
import 'svelte/internal/flags/legacy';
import * as $ from 'svelte/internal/client';
import { getContext } from 'svelte';

let busy = false;
function animate(fn) {
    if (!busy) {
        busy = true;
        window.requestAnimationFrame(() => {
            fn();
            busy = false;
        });
    }
}

function limit(value, minLimit, maxLimit) {
    return max(minLimit, min(maxLimit, value));
}

function Action($$anchor, $$props) {
	$.push($$props, false);

	const [$$stores, $$cleanup] = $.setup_stores();
	const $_draggable = () => $.store_get(_draggable, '$_draggable', $$stores);
	const $slotDuration = () => $.store_get(slotDuration, '$slotDuration', $$stores);
	const $selectable = () => $.store_get(selectable, '$selectable', $$stores);
	const $view = () => $.store_get(view, '$view', $$stores);
	const $_bodyEl = () => $.store_get(_bodyEl, '$_bodyEl', $$stores);
	const $datesAboveResources = () => $.store_get(datesAboveResources, '$datesAboveResources', $$stores);
	const $selectLongPressDelay = () => $.store_get(selectLongPressDelay, '$selectLongPressDelay', $$stores);
	const $eventLongPressDelay = () => $.store_get(eventLongPressDelay, '$eventLongPressDelay', $$stores);
	const $longPressDelay = () => $.store_get(longPressDelay, '$longPressDelay', $$stores);
	const $selectMinDistance = () => $.store_get(selectMinDistance, '$selectMinDistance', $$stores);
	const $eventDragMinDistance = () => $.store_get(eventDragMinDistance, '$eventDragMinDistance', $$stores);
	const $_iEvents = () => $.store_get(_iEvents, '$_iEvents', $$stores);
	const $eventResizeStart = () => $.store_get(eventResizeStart, '$eventResizeStart', $$stores);
	const $eventDragStart = () => $.store_get(eventDragStart, '$eventDragStart', $$stores);
	const $dragScroll = () => $.store_get(dragScroll, '$dragScroll', $$stores);
	const $slotHeight = () => $.store_get(slotHeight, '$slotHeight', $$stores);
	const $slotWidth = () => $.store_get(slotWidth, '$slotWidth', $$stores);
	const $unselectAuto = () => $.store_get(unselectAuto, '$unselectAuto', $$stores);
	const $unselectCancel = () => $.store_get(unselectCancel, '$unselectCancel', $$stores);
	const $selectFn = () => $.store_get(selectFn, '$selectFn', $$stores);
	const $_view = () => $.store_get(_view, '$_view', $$stores);
	const $eventResizeStop = () => $.store_get(eventResizeStop, '$eventResizeStop', $$stores);
	const $eventDragStop = () => $.store_get(eventDragStop, '$eventDragStop', $$stores);
	const $eventResize = () => $.store_get(eventResize, '$eventResize', $$stores);
	const $eventDrop = () => $.store_get(eventDrop, '$eventDrop', $$stores);
	const $dateClick = () => $.store_get(dateClick, '$dateClick', $$stores);
	const $validRange = () => $.store_get(validRange, '$validRange', $$stores);
	const $_dayGrid = () => $.store_get(_dayGrid, '$_dayGrid', $$stores);
	const $_events = () => $.store_get(_events, '$_events', $$stores);
	const $selectBackgroundColor = () => $.store_get(selectBackgroundColor, '$selectBackgroundColor', $$stores);
	const $unselectFn = () => $.store_get(unselectFn, '$unselectFn', $$stores);

	let {
		_iEvents,
		_iClass,
		_events,
		_view,
		_dayGrid,
		_draggable,
		_bodyEl,
		dateClick,
		dragScroll,
		datesAboveResources,
		eventDragMinDistance,
		eventDragStart,
		eventDragStop,
		eventDrop,
		eventLongPressDelay,
		eventResizeStart,
		eventResizeStop,
		eventResize,
		longPressDelay,
		selectable,
		select: selectFn,
		selectBackgroundColor,
		selectLongPressDelay,
		selectMinDistance,
		slotDuration,
		slotHeight,
		slotWidth,
		unselect: unselectFn,
		unselectAuto,
		unselectCancel,
		validRange,
		view
	} = getContext('state');

	const ACTION_DRAG = 1;
	const ACTION_RESIZE_END = 2;
	const ACTION_RESIZE_START = 3;
	const ACTION_SELECT = 4;
	const ACTION_CLICK = 5;
	const ACTION_NO_ACTION = 6;
	let action;
	let interacting;
	let event;
	let display;
	let date, newDate;
	let resource, newResource;
	let fromX, fromY;
	let toX, toY;
	let bodyEl, bodyRect, clipEl, clipRect;
	let delta;
	let allDay;
	let iClass;
	let minResize; // minimum end time when resizing
	let selectStep; // minimum selection step
	let selected; // whether selection has been made
	let noDateClick; // do not perform date click
	let timer = $.mutable_state(); // timer for long press delays
	let viewport;
	let margin;
	let extraDuration; // extra duration for zero duration events

	function drag(
		eventToDrag,
		jsEvent,
		resize,
		forceDate,
		forceMargin,
		zeroDuration
	) {
		if (!action) {
			action = validJsEvent(jsEvent) ? resize ? resize[1] == 'start' ? ACTION_RESIZE_START : ACTION_RESIZE_END : $_draggable()(eventToDrag) ? ACTION_DRAG : ACTION_NO_ACTION : ACTION_NO_ACTION;

			if (complexAction()) {
				event = eventToDrag;
				common(jsEvent);

				if (forceDate) {
					// Force date in popup
					date = forceDate;
				}

				if (forceMargin) {
					margin = forceMargin;
				}

				iClass = resize ? resize[0] == 'x' ? 'resizingX' : 'resizingY' : 'dragging';

				if (resize) {
					// Calculate the limits for resize
					if (resizingStart()) {
						minResize = cloneDate(event.end);

						if (allDay) {
							copyTime(minResize, event.start);

							if (minResize >= event.end) {
								subtractDay(minResize);
							}
						} else {
							if (!zeroDuration) {
								subtractDuration(minResize, $slotDuration());
							}

							// Overwrite the date due to possible discrepancy between calculated date
							// and resizer coordinates in browser
							date = event.start;
						}
					} else {
						minResize = cloneDate(event.start);

						if (allDay) {
							copyTime(minResize, event.end);

							if (minResize <= event.start && !zeroDuration) {
								addDay(minResize);
							}
						} else {
							addDuration(minResize, $slotDuration());
							// Overwrite the date due to possible discrepancy between calculated date
							// and resizer coordinates in browser
							date = event.end;
						}
					}

					// Handle zero duration events
					if (zeroDuration && !allDay) {
						extraDuration = $slotDuration();
					}
				}

				move(jsEvent);
			}
		}
	}

	function select(jsEvent) {
		if (!action) {
			action = validJsEvent(jsEvent) ? $selectable() && !listView($view()) ? ACTION_SELECT : ACTION_CLICK : ACTION_NO_ACTION;

			if (complexAction()) {
				common(jsEvent);
				iClass = 'selecting';
				selectStep = allDay ? createDuration({ day: 1 }) : $slotDuration();

				// Create dummy source event
				event = {
					allDay,
					start: date,
					end: addDuration(cloneDate(date), selectStep),
					resourceIds: resource ? [resource.id] : []
				};

				move(jsEvent);
			}
		}
	}

	function noAction() {
		if (!action) {
			action = ACTION_NO_ACTION;
		}
	}

	function common(jsEvent) {
		window.getSelection().removeAllRanges();
		fromX = toX = jsEvent.clientX;
		fromY = toY = jsEvent.clientY;

		let dayEl = getElementWithPayload(toX, toY);

		({ allDay, date, resource } = getPayload(dayEl)(toX, toY));

		if (timelineView($view())) {
			bodyEl = clipEl = $_bodyEl();
		} else {
			bodyEl = ancestor(dayEl, resource ? 4 : 3);
			clipEl = ancestor(dayEl, resource && (dragging() || $datesAboveResources()) ? 2 : 1);
		}

		calcViewport();

		if (jsEvent.pointerType !== 'mouse') {
			// For touch devices init long press delay
			$.set(timer, setTimeout(
				() => {
					if (action) {
						interacting = true;
						move(jsEvent);
					}
				},
				(selecting() ? $selectLongPressDelay() : $eventLongPressDelay()) ?? $longPressDelay()
			));
		}
	}

	function move(jsEvent) {
		if (interacting || jsEvent && jsEvent.pointerType === 'mouse' && distance() >= (selecting() ? $selectMinDistance() : $eventDragMinDistance())) {
			interacting = true;
			unselect(jsEvent);
			$.store_set(_iClass, iClass);

			if (!$_iEvents()[0]) {
				if (selecting()) {
					createIEventSelect();
				} else {
					createIEvent(jsEvent, resizing() ? $eventResizeStart() : $eventDragStart());
				}
			}

			let payload = findPayload(findDayEl());

			if (payload) {
				let newAllDay;

				({
					allDay: newAllDay,
					date: newDate,
					resource: newResource
				} = payload);

				if (newAllDay === allDay) {
					delta = createDuration((newDate - date) / 1000);

					if (resizingStart()) {
						// Resizing start
						$.store_mutate(_iEvents, $.untrack($_iEvents)[0].start = addDuration(cloneDate(event.start), delta), $.untrack($_iEvents));

						if ($_iEvents()[0].start > minResize) {
							$.store_mutate(_iEvents, $.untrack($_iEvents)[0].start = minResize, $.untrack($_iEvents));
							delta = createDuration((minResize - event.start) / 1000);
						}
					} else {
						$.store_mutate(_iEvents, $.untrack($_iEvents)[0].end = addDuration(cloneDate(event.end), delta), $.untrack($_iEvents));

						if (extraDuration) {
							addDuration($_iEvents()[0].end, extraDuration);
						}

						if (resizing()) {
							// Resizing end
							if ($_iEvents()[0].end < minResize) {
								$.store_mutate(_iEvents, $.untrack($_iEvents)[0].end = minResize, $.untrack($_iEvents));
								delta = createDuration((minResize - event.end) / 1000);
							}
						} else if (selecting()) {
							// Selecting
							if ($_iEvents()[0].end < event.end) {
								$.store_mutate(_iEvents, $.untrack($_iEvents)[0].start = subtractDuration($_iEvents()[0].end, selectStep), $.untrack($_iEvents));
								$.store_mutate(_iEvents, $.untrack($_iEvents)[0].end = event.end, $.untrack($_iEvents));
							} else {
								$.store_mutate(_iEvents, $.untrack($_iEvents)[0].start = event.start, $.untrack($_iEvents));
							}
						} else {
							// Dragging
							$.store_mutate(_iEvents, $.untrack($_iEvents)[0].start = addDuration(cloneDate(event.start), delta), $.untrack($_iEvents));

							if (resource) {
								$.store_mutate(_iEvents, $.untrack($_iEvents)[0].resourceIds = event.resourceIds.filter((id) => id !== resource.id), $.untrack($_iEvents));
								$_iEvents()[0].resourceIds.push(newResource.id);
							}
						}
					}
				}
			}
		}

		if ($dragScroll()) {
			let thresholdY = $slotHeight() * 2;
			let thresholdX = $slotWidth();

			animate(() => {
				if (bodyEl) {
					if (toY < thresholdY) {
						window.scrollBy(0, max(-10, (toY - thresholdY) / 3));
					}

					if (toY < bodyRect.top + thresholdY) {
						bodyEl.scrollTop += max(-10, (toY - bodyRect.top - thresholdY) / 3);
					}

					if (toY > window.innerHeight - thresholdY) {
						window.scrollBy(0, min(10, (toY - window.innerHeight + thresholdY) / 3));
					}

					if (toY > bodyRect.bottom - thresholdY) {
						bodyEl.scrollTop += min(10, (toY - bodyRect.bottom + thresholdY) / 3);
					}

					if (timelineView($view())) {
						if (toX < bodyRect.left + thresholdX) {
							bodyEl.scrollLeft += max(-10, (toX - bodyRect.left - thresholdX) / 3);
						}

						if (toX > bodyRect.right - thresholdX) {
							bodyEl.scrollLeft += min(10, (toX - bodyRect.right + thresholdX) / 3);
						}
					}
				}
			});
		}
	}

	function handleScroll() {
		if (complexAction()) {
			calcViewport();
			move();
		}
	}

	function handlePointerMove(jsEvent) {
		if (complexAction() && jsEvent.isPrimary) {
			toX = jsEvent.clientX;
			toY = jsEvent.clientY;
			move(jsEvent);
		}
	}

	function handlePointerUp(jsEvent) {
		if (selected && $unselectAuto() && !($unselectCancel() && jsEvent.target.closest($unselectCancel()))) {
			unselect(jsEvent);
		}

		if (action && jsEvent.isPrimary) {
			if (interacting) {
				if (selecting()) {
					selected = true;

					if (isFunction($selectFn())) {
						let { start, end } = toEventWithLocalDates($_iEvents()[0]);

						$selectFn()({
							start,
							end,
							startStr: toISOString($_iEvents()[0].start),
							endStr: toISOString($_iEvents()[0].end),
							allDay,
							jsEvent,
							view: toViewWithLocalDates($_view()),
							resource
						});
					}
				} else {
					event.display = display;

					let callback = resizing() ? $eventResizeStop() : $eventDragStop();

					if (isFunction(callback)) {
						callback({
							event: toEventWithLocalDates(event),
							jsEvent,
							view: toViewWithLocalDates($_view())
						});
					}

					let oldEvent = cloneEvent(event);

					updateEvent(event, $_iEvents()[0]);
					destroyIEvent();
					callback = resizing() ? $eventResize() : $eventDrop();

					if (isFunction(callback)) {
						let eventRef = event;
						let info;

						if (resizing()) {
							info = { endDelta: delta };
						} else {
							info = {
								delta,
								oldResource: resource !== newResource ? resource : undefined,
								newResource: resource !== newResource ? newResource : undefined
							};
						}

						callback(assign(info, {
							event: toEventWithLocalDates(event),
							oldEvent: toEventWithLocalDates(oldEvent),
							jsEvent,
							view: toViewWithLocalDates($_view()),
							revert() {
								updateEvent(eventRef, oldEvent);
							}
						}));
					}
				}
			} else {
				if (clicking() || selecting()) {
					// Perform date click
					if (isFunction($dateClick()) && !noDateClick) {
						toX = jsEvent.clientX;
						toY = jsEvent.clientY;

						let dayEl = getElementWithPayload(toX, toY);

						if (dayEl) {
							let { allDay, date, resource } = getPayload(dayEl)(toX, toY);

							$dateClick()({
								allDay,
								date: toLocalDate(date),
								dateStr: toISOString(date),
								dayEl,
								jsEvent,
								view: toViewWithLocalDates($_view()),
								resource
							});
						}
					}
				}
			}

			interacting = false;
			action = fromX = fromY = toX = toY = event = display = date = newDate = resource = newResource = delta = extraDuration = allDay = $.store_set(_iClass, minResize = selectStep = margin = undefined);
			bodyEl = clipEl = bodyRect = clipRect = undefined;

			if ($.get(timer)) {
				clearTimeout($.get(timer));
				$.set(timer, undefined);
			}
		}

		noDateClick = false;
	}

	function findDayEl() {
		// Limit coordinates to viewport
		return getElementWithPayload(limit(toX, viewport[0], viewport[1]), limit(toY, viewport[2], viewport[3]));
	}

	function findPayload(dayEl) {
		if (dayEl) {
			let payload = getPayload(dayEl)(toX, toY);

			if (payload.disabled) {
				if (!$validRange().end || payload.date < $validRange().end) {
					return findPayload(dayEl.nextElementSibling);
				}

				if (!$validRange().start || payload.date > $validRange().start) {
					return findPayload(dayEl.previousElementSibling);
				}
			} else {
				return payload;
			}
		}

		return null;
	}

	function calcViewport() {
		bodyRect = rect(bodyEl);
		clipRect = rect(clipEl);

		viewport = [
			max(0, clipRect.left + (timelineView($view()) ? 1 : $_dayGrid() ? 0 : 8)), // left
			min(document.documentElement.clientWidth, clipRect.left + clipEl.clientWidth) - 2, // right
			max(0, bodyRect.top), // top
			min(document.documentElement.clientHeight, bodyRect.top + bodyEl.clientHeight) - 2 // bottom
		];
	}

	function createIEvent(jsEvent, callback) {
		if (isFunction(callback)) {
			callback({
				event: toEventWithLocalDates(event),
				jsEvent,
				view: toViewWithLocalDates($_view())
			});
		}

		display = event.display;
		event.display = 'preview';
		$.store_mutate(_iEvents, $.untrack($_iEvents)[0] = cloneEvent(event), $.untrack($_iEvents));

		if (margin !== undefined) {
			$.store_mutate(_iEvents, $.untrack($_iEvents)[0]._margin = margin, $.untrack($_iEvents));
		}

		if (extraDuration) {
			addDuration($_iEvents()[0].end, extraDuration);
		}

		event.display = 'ghost';
		$.store_set(_events, $_events());
	}

	function createIEventSelect() {
		$.store_mutate(
			_iEvents,
			$.untrack($_iEvents)[0] = {
				id: '{select}',
				allDay: event.allDay,
				start: event.start,
				title: '',
				display: 'preview',
				extendedProps: {},
				backgroundColor: $selectBackgroundColor(),
				resourceIds: event.resourceIds,
				classNames: [],
				styles: []
			},
			$.untrack($_iEvents)
		);
	}

	function destroyIEvent() {
		$.store_mutate(_iEvents, $.untrack($_iEvents)[0] = null, $.untrack($_iEvents));
	}

	function updateEvent(target, source) {
		target.start = source.start;
		target.end = source.end;
		target.resourceIds = source.resourceIds;
		$.store_set(_events, $_events());
	}

	function distance() {
		return Math.sqrt(Math.pow(toX - fromX, 2) + Math.pow(toY - fromY, 2));
	}

	function dragging() {
		return action === ACTION_DRAG;
	}

	function resizing() {
		return action === ACTION_RESIZE_END || resizingStart();
	}

	function resizingStart() {
		return action === ACTION_RESIZE_START;
	}

	function clicking() {
		return action === ACTION_CLICK;
	}

	function selecting() {
		return action === ACTION_SELECT;
	}

	function complexAction() {
		return action && action < ACTION_CLICK;
	}

	function validJsEvent(jsEvent) {
		return jsEvent.isPrimary && (jsEvent.pointerType !== 'mouse' || jsEvent.buttons & 1);
	}

	function unselect(jsEvent) {
		if (selected) {
			selected = false;
			destroyIEvent();

			if (isFunction($unselectFn())) {
				$unselectFn()({
					jsEvent,
					view: toViewWithLocalDates($_view())
				});
			}
		}
	}

	function noClick() {
		noDateClick = true;
	}

	// Clear selection on view params change
	_view.subscribe(unselect);

	function handleTouchStart(jsEvent) {
		if (complexAction()) {
			let target = jsEvent.target;
			let stops = [];
			let stop = () => runAll(stops);

			stops.push(listen(target, 'touchmove', createPreventDefaultHandler(() => interacting)));
			stops.push(listen(target, 'touchend', stop));
			stops.push(listen(target, 'touchcancel', stop));
		}
	}

	function createPreventDefaultHandler(condition) {
		return (jsEvent) => {
			if (condition()) {
				jsEvent.preventDefault();
			}
		};
	}

	$.init();
	$.event('pointermove', $.window, handlePointerMove);
	$.event('pointerup', $.window, handlePointerUp);
	$.event('pointercancel', $.window, handlePointerUp);
	$.event('scroll', $.window, handleScroll);

	var event_handler = $.derived(() => createPreventDefaultHandler(complexAction));

	$.event('selectstart', $.window, function (...$$args) {
		$.get(event_handler)?.apply(this, $$args);
	});

	var event_handler_1 = $.derived(() => createPreventDefaultHandler(() => $.get(timer)));

	$.event('contextmenu', $.window, function (...$$args) {
		$.get(event_handler_1)?.apply(this, $$args);
	});

	$.event('touchstart', $.window, handleTouchStart);

	$.event(
		'touchmove',
		$.window,
		function ($$arg) {
			$.bubble_event.call(this, $$props, $$arg);
		},
		undefined,
		false
	);

	$.bind_prop($$props, 'drag', drag);
	$.bind_prop($$props, 'select', select);
	$.bind_prop($$props, 'noAction', noAction);
	$.bind_prop($$props, 'handleScroll', handleScroll);
	$.bind_prop($$props, 'unselect', unselect);
	$.bind_prop($$props, 'noClick', noClick);

	var $$pop = $.pop({
		drag,
		select,
		noAction,
		handleScroll,
		unselect,
		noClick
	});

	$$cleanup();
	return $$pop;
}

function Pointer($$anchor, $$props) {
	$.push($$props, false);

	const [$$stores, $$cleanup] = $.setup_stores();
	const $_iEvents = () => $.store_get(_iEvents, '$_iEvents', $$stores);
	const $slotDuration = () => $.store_get(slotDuration, '$slotDuration', $$stores);
	let { _iEvents, slotDuration } = getContext('state');
	let x = 0, y = 0;

	function move() {
		let dayEl = getElementWithPayload(x, y);

		if (dayEl) {
			let { allDay, date, resource, disabled } = getPayload(dayEl)(x, y);

			if (!disabled) {
				let idx = allDay ? 2 : 1;

				if (!$_iEvents()[idx]) {
					createPointerEvent(idx);
				}

				$.store_mutate(_iEvents, $.untrack($_iEvents)[idx].start = date, $.untrack($_iEvents));
				$.store_mutate(_iEvents, $.untrack($_iEvents)[idx].end = addDuration(cloneDate(date), $slotDuration()), $.untrack($_iEvents));
				$.store_mutate(_iEvents, $.untrack($_iEvents)[idx].resourceIds = resource ? [resource.id] : [], $.untrack($_iEvents));
				return;
			}
		}

		removePointerEvent();
	}

	function handleScroll() {
		move();
	}

	function handlePointerMove(jsEvent) {
		if (validEvent(jsEvent)) {
			x = jsEvent.clientX;
			y = jsEvent.clientY;
			move();
		}
	}

	function createPointerEvent(idx) {
		$.store_mutate(
			_iEvents,
			$.untrack($_iEvents)[idx] = {
				id: '{pointer}',
				title: '',
				display: 'pointer',
				extendedProps: {},
				backgroundColor: 'transparent',
				classNames: [],
				styles: []
			},
			$.untrack($_iEvents)
		);
	}

	function removePointerEvent() {
		if ($_iEvents()[1]) {
			$.store_mutate(_iEvents, $.untrack($_iEvents)[1] = null, $.untrack($_iEvents));
		}

		if ($_iEvents()[2]) {
			$.store_mutate(_iEvents, $.untrack($_iEvents)[2] = null, $.untrack($_iEvents));
		}
	}

	function validEvent(jsEvent) {
		return jsEvent.isPrimary && jsEvent.pointerType === 'mouse';
	}

	$.legacy_pre_effect(() => ($_iEvents()), () => {
		if ($_iEvents()[0]) {
			removePointerEvent();
		}
	});

	$.legacy_pre_effect_reset();
	$.init();
	$.event('pointermove', $.window, handlePointerMove);
	$.event('scroll', $.window, handleScroll);
	$.bind_prop($$props, 'handleScroll', handleScroll);

	var $$pop = $.pop({ handleScroll });

	$$cleanup();
	return $$pop;
}

var root_1 = $.template(`<div></div>`);

function Resizer($$anchor, $$props) {
	$.push($$props, true);

	const [$$stores, $$cleanup] = $.setup_stores();
	const $eventResizableFromStart = () => $.store_get(eventResizableFromStart, '$eventResizableFromStart', $$stores);
	const $eventDurationEditable = () => $.store_get(eventDurationEditable, '$eventDurationEditable', $$stores);
	const $editable = () => $.store_get(editable, '$editable', $$stores);
	const $theme = () => $.store_get(theme, '$theme', $$stores);
	const start = $.prop($$props, 'start', 3, false);

	let {
		theme,
		eventDurationEditable,
		eventResizableFromStart,
		editable
	} = getContext('state');

	const resizable = $.derived(() => !bgEvent($$props.event.display) && !helperEvent($$props.event.display) && (!start() || $eventResizableFromStart()) && (($$props.event.durationEditable ?? $eventDurationEditable()) || ($$props.event.editable ?? $editable())));
	var fragment = $.comment();
	var node = $.first_child(fragment);

	{
		var consequent = ($$anchor) => {
			var div = root_1();

			div.__pointerdown = function (...$$args) {
				$$props.onpointerdown?.apply(this, $$args);
			};

			$.template_effect(() => $.set_class(div, `${$theme().resizer ?? ''}${(start() ? ' ' + $theme().start : '') ?? ''}`));
			$.append($$anchor, div);
		};

		$.if(node, ($$render) => {
			if ($.get(resizable)) $$render(consequent);
		});
	}

	$.append($$anchor, fragment);
	$.pop();
	$$cleanup();
}

$.delegate(['pointerdown']);

var root = $.template(`<!> <!>`, 1);

function Auxiliary($$anchor, $$props) {
	$.push($$props, false);

	const [$$stores, $$cleanup] = $.setup_stores();
	const $_interaction = () => $.store_get(_interaction, '$_interaction', $$stores);
	const $_draggable = () => $.store_get(_draggable, '$_draggable', $$stores);
	const $eventStartEditable = () => $.store_get(eventStartEditable, '$eventStartEditable', $$stores);
	const $editable = () => $.store_get(editable, '$editable', $$stores);
	const $theme = () => $.store_get(theme, '$theme', $$stores);
	const $_bodyEl = () => $.store_get(_bodyEl, '$_bodyEl', $$stores);
	const $pointer = () => $.store_get(pointer, '$pointer', $$stores);

	let {
		theme,
		editable,
		eventStartEditable,
		pointer,
		_bodyEl,
		_interaction,
		_iClasses,
		_draggable
	} = getContext('state');

	$.store_mutate(_interaction, $.untrack($_interaction).resizer = Resizer, $.untrack($_interaction));

	function bodyScrollHandler() {
		for (let component of Object.values($_interaction())) {
			component?.handleScroll?.();
		}
	}

	$.legacy_pre_effect(() => ($eventStartEditable(), $editable()), () => {
		$.store_set(_draggable, (event) => (event.startEditable ?? $eventStartEditable()) || (event.editable ?? $editable()));
	});

	$.legacy_pre_effect(
		() => (
			$theme(),
			$_draggable()
		),
		() => {
			$.store_set(_iClasses, (className, event) => {
				let { display } = event;

				return helperEvent(display) ? [$theme()[display]] : !bgEvent(display) && $_draggable()(event) ? [$theme().draggable] : [];
			});
		}
	);

	$.legacy_pre_effect(() => ($_bodyEl(), listen), () => {
		if ($_bodyEl()) {
			listen($_bodyEl(), 'scroll', bodyScrollHandler);
		}
	});

	$.legacy_pre_effect_reset();
	$.init();

	var fragment = root();
	var node = $.first_child(fragment);

	$.bind_this(Action(node, { $$legacy: true }), ($$value) => $.store_mutate(_interaction, $.untrack($_interaction).action = $$value, $.untrack($_interaction)), () => $_interaction()?.action);

	var node_1 = $.sibling(node, 2);

	{
		var consequent = ($$anchor) => {
			$.bind_this(Pointer($$anchor, { $$legacy: true }), ($$value) => $.store_mutate(_interaction, $.untrack($_interaction).pointer = $$value, $.untrack($_interaction)), () => $_interaction()?.pointer);
		};

		$.if(node_1, ($$render) => {
			if ($pointer()) $$render(consequent);
		});
	}

	$.append($$anchor, fragment);
	$.pop();
	$$cleanup();
}

var index = {
    createOptions(options) {
        options.dateClick = undefined;
        options.dragScroll = true;
        options.editable = false;
        options.eventDragMinDistance = 5;
        options.eventDragStart = undefined;
        options.eventDragStop = undefined;
        options.eventDrop = undefined;
        options.eventDurationEditable = true;
        options.eventLongPressDelay = undefined;
        options.eventResizableFromStart = false;
        options.eventResizeStart = undefined;
        options.eventResizeStop = undefined;
        options.eventResize = undefined;
        options.eventStartEditable = true;
        options.longPressDelay = 1000;
        options.pointer = false;
        options.select = undefined;
        options.selectBackgroundColor = undefined;  // ec option
        options.selectLongPressDelay = undefined;
        options.selectMinDistance = 5;
        options.unselect = undefined;
        options.unselectAuto = true;
        options.unselectCancel = '';
        options.theme.draggable = 'ec-draggable';
        options.theme.ghost = 'ec-ghost';
        options.theme.preview = 'ec-preview';
        options.theme.pointer = 'ec-pointer';
        options.theme.resizer = 'ec-resizer';
        options.theme.start = 'ec-start';
        options.theme.dragging = 'ec-dragging';
        options.theme.resizingY = 'ec-resizing-y';
        options.theme.resizingX = 'ec-resizing-x';
        options.theme.selecting = 'ec-selecting';
    },

    createStores(state) {
        state._draggable = writable(noop);
        state._auxiliary.update($_auxiliary => [...$_auxiliary, Auxiliary]);
    }
};

export { index as default };
